import { useMemo } from 'react';
import { usePlayFactoryData, PLAY_FACTORY_PROVIDER } from './usePlayFactoryData';
import useLicenseAcquisitionUrl, { ArcusLaUrlInput, ARCUS_DRM_PROVIDER } from './useLicenseAcquisitionUrl';
import { SimpleAsset, Status } from '../utils/types';
import { isPlayFactoryAvailable, MediaUrls } from '../utils/playFactoryApi';

export interface UseDrmMediaUrlsInput {
	drmHlsSource?: SimpleAsset;
	status?: Status;
}

export interface DrmMediaUrls extends MediaUrls {
	isLoading: boolean;
	source: typeof PLAY_FACTORY_PROVIDER | typeof ARCUS_DRM_PROVIDER | 'default';
}

export const useDrmMediaUrls = (input: UseDrmMediaUrlsInput): DrmMediaUrls => {
	const { drmHlsSource, status } = input;

	const isLive = useMemo(() => {
		return status?.toLocaleLowerCase() === Status.LIVE.toLocaleLowerCase() || status?.toLocaleLowerCase() === 'in_progress';
	}, [status]);

	const { mediaData: playFactoryData, isLoading: playFactoryLoading } = usePlayFactoryData(isLive ? drmHlsSource : undefined);

	const usePlayFactory = useMemo(() => {
		return isPlayFactoryAvailable() && isLive && !playFactoryData.expirations?.license?.expired;
	}, [isLive, playFactoryData]);

	const arcusInput = useMemo<ArcusLaUrlInput | undefined>(() => {
		if (!usePlayFactory && drmHlsSource) {
			return {
				configAlias: drmHlsSource.configAlias,
				contentRef: drmHlsSource.contentRef,
				propositionContentId: drmHlsSource.propositionContentId,
				protection: drmHlsSource.protection,
				drmProvider: ARCUS_DRM_PROVIDER
			};
		}
		return undefined;
	}, [usePlayFactory, drmHlsSource]);

	const { laUrl: arcusLaUrl, loading: arcusLoading } = useLicenseAcquisitionUrl(arcusInput);

	if (usePlayFactory) {
		return {
			videoUrl: playFactoryData!.videoUrl,
			licenseUrl: playFactoryData!.licenseUrl,
			expirations: playFactoryData!.expirations,
			isLoading: playFactoryLoading,
			source: PLAY_FACTORY_PROVIDER
		};
	}

	if (arcusLaUrl) {
		return {
			videoUrl: drmHlsSource?.url,
			licenseUrl: arcusLaUrl,
			expirations: undefined,
			isLoading: arcusLoading,
			source: ARCUS_DRM_PROVIDER
		};
	}

	return {
		videoUrl: drmHlsSource?.url,
		licenseUrl: undefined,
		expirations: undefined,
		isLoading: playFactoryLoading || arcusLoading,
		source: 'default'
	};
};
